import React, { memo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Map } from 'immutable';

import FormGroup from '../../helpers/FormGroup';
import BaseInput from '../../helpers/Input';
import { InputProps } from '../../helpers/AutoComplete';
import Span from '../../helpers/Span';
import BaseSelect from '../../helpers/Select';
import { getIn } from '../../../utils/ChangeSpy';
import { isResearcher } from '../../../utils/checkPermissions';
import If from '../../helpers/If';

import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import { CompanyNDA } from '../CompanyNDA';
import { usePopupContext } from '../../../contexts/PopupContext';
import { Card } from '../../helpers/Card';
import Button from '../../helpers/Button';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));
const Select = PrettyError(DisplayError(BaseSelect));

const CompanyStatusComponent = props => {
  const { openPopup } = usePopupContext();
  const {
    isUnapproved,
    companyInfo,
    suggestAnalyst,
    suggestDealMaker,
    suggestDirector,
    statuses,
    onChange,
    onRef,
    hasPermissions,
    currentStatus,
    user,
    canEditData,
    lastEventBuyerText,
    isLoadingLeadMemoReport,
    onLeadMemoReport,
    isDuplicateCompany,
  } = props;
  const highStatus = getIn(companyInfo, 'buyers.0.highStatus');
  const statusIndex = statuses.findIndex(status => getIn(status, ['value']) === highStatus);
  const title =
    statusIndex === -1 ? statuses.getIn([0, 'name'], 'Statuses not found') : statuses.getIn([statusIndex, 'name']);
  const highestStatus = getIn(companyInfo, 'highestStatus.value');
  const researcher = isResearcher(user.getIn(['roles', 0, 'slug'], null));
  let disabledStatuses = false;
  let defaultOption;
  let highestStatusText;

  if (highestStatus >= 5) {
    highestStatusText = Map({
      highestStatus: researcher ? 'Dialog' : highestStatus,
    });
  } else if (highestStatus >= 3) {
    highestStatusText = Map({
      highestStatus: researcher ? 'Research Complete' : highestStatus,
    });
  }

  if ((hasPermissions && isUnapproved) || (!hasPermissions && currentStatus > 1.4)) {
    disabledStatuses = true;

    if (currentStatus > 1.4 && currentStatus < 2) {
      defaultOption = 'Research Complete; Needs Internal Review';
    } else if (currentStatus >= 2 && currentStatus < 2.99) {
      defaultOption = 'Client No Interest';
    } else if (currentStatus >= 2.99 && currentStatus <= 10) {
      defaultOption = 'Research Complete';
    }
  }

  if (isDuplicateCompany) {
    disabledStatuses = true;
  }

  /** Open request NDA popup. */
  const openNdaPopup = () => {
    const { companyId } = props;

    openPopup('NdaRequestPopup', { companyId });
  };

  /** Open request NDA popup. */
  const openLeadMemo = () => {
    const { companyId } = props;

    onLeadMemoReport(companyId);
  };

  return (
    <Card ref={onRef} as="form" className={classNames(style.host, 'd-flex form-inline')}>
      <FormGroup className="pl0 w160 form-inline-block">
        <label className="field-label">Module</label>
        <InputProps
          className="form-control input-sm"
          disabled={!canEditData}
          name="target.suggestDirector"
          placeholder="Module"
        >
          {suggestDirector}
        </InputProps>
      </FormGroup>
      <FormGroup className="w160 form-inline-block">
        <label className="field-label">Dealmaker 1</label>
        <InputProps
          className="form-control input-sm"
          disabled={!canEditData}
          name="target.suggestAnalyst"
          placeholder="Dealmaker 1"
        >
          {suggestAnalyst}
        </InputProps>
      </FormGroup>
      <FormGroup className="w160 form-inline-block">
        <label className="field-label">Dealmaker 2</label>
        <InputProps
          className="form-control input-sm"
          disabled={!canEditData}
          name="target.suggestDealMaker"
          placeholder="Dealmaker 2"
        >
          {suggestDealMaker}
        </InputProps>
      </FormGroup>
      <FormGroup className="w260 form-inline-block">
        <Select
          defaultText={defaultOption}
          disabled={disabledStatuses}
          emptyValue=""
          label="Status"
          name="currentStatus"
          onChange={onChange}
          options={statuses}
          prefix="buyers.0"
          root={companyInfo}
          selectClassName="input-sm"
          deep
          showLabel
          showValue
        />
      </FormGroup>
      <FormGroup className="w160 form-inline-block">
        <Input
          className="input-sm"
          label="High"
          name="highestStatus"
          placeholder="High"
          root={highestStatusText || companyInfo}
          title={title}
          deep
          readOnly
          showLabel
        />
      </FormGroup>
      <If value={hasPermissions}>
        <FormGroup className="form-inline-block">
          <div className={style.ndaBlock}>
            <CompanyNDA company={companyInfo} />
            <Button disabled={isDuplicateCompany} onClick={openNdaPopup}>
              NDA
            </Button>
            <Button disabled={isLoadingLeadMemoReport || isDuplicateCompany} onClick={openLeadMemo}>
              Lead Memo
            </Button>
          </div>
        </FormGroup>
      </If>
      <FormGroup className="flex-1 pr0 form-inline-block text-right">
        <Span deep="children" name="lastEventBuyerText" root={lastEventBuyerText} />
      </FormGroup>
      <div className="clearfix" />
    </Card>
  );
};

CompanyStatusComponent.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

export const CompanyStatus = memo(CompanyStatusComponent);
